<template>
    <div>
        <!-- Most Confused Items Section -->
        <div v-if="type === 'confused_with' && mostConfusedPairsData.length > 0" style="margin-bottom: 30px; padding: 20px; background-color: #f5f5f5; border-radius: 4px;">
            <div style="font-size: 1.25rem; color: #666; margin-bottom: 15px; font-weight: 500;">
                {{ $t('confusion_matrix.most_confused_items') }}
            </div>
            <row>
                <col4 v-for="(pair, index) in mostConfusedPairsData" :key="index" style="margin-bottom: 20px;">
                    <div style="background-color: white; padding: 15px; border-radius: 4px; border: 1px solid #ddd;">
                        <div style="font-weight: 500; margin-bottom: 10px; text-align: center; color: #333;">
                            {{ pair.originalLabel }} → {{ pair.confusedLabel }}
                            <span style="color: #666; font-size: 0.9em;">({{ pair.count }})</span>
                        </div>
                        <row>
                            <col6>
                                <div style="margin-bottom: 5px; font-size: 0.85em; color: #666; text-align: center;">
                                    Predicted
                                </div>
                                <div v-for="(example, idx) in pair.predictedExamples.slice(0, 4)" :key="'p'+idx" class="cm-example" :class="{ 'cm-example--blur': sensitiveBlurForConfusionCrop(example) }">
                                    <img 
                                        v-if="example.data" 
                                        :src="'data:image/png;base64, ' + example.data" 
                                        style="width: 100%; max-height: 100px; object-fit: contain; border: 1px solid #ddd; border-radius: 2px;"
                                    />
                                    <div v-else style="width: 100%; height: 100px; background-color: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 2px; color: #999;">
                                        Loading...
                                    </div>
                                </div>
                            </col6>
                            <col6>
                                <div style="margin-bottom: 5px; font-size: 0.85em; color: #666; text-align: center;">
                                    Confused
                                </div>
                                <div v-for="(example, idx) in pair.confusedExamples.slice(0, 3)" :key="'c'+idx" class="cm-example" :class="{ 'cm-example--blur': sensitiveBlurForConfusionCrop(example) }">
                                    <img 
                                        v-if="example.data" 
                                        :src="'data:image/png;base64, ' + example.data" 
                                        style="width: 100%; max-height: 100px; object-fit: contain; border: 1px solid #ddd; border-radius: 2px;"
                                    />
                                    <div v-else style="width: 100%; height: 100px; background-color: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 2px; color: #999;">
                                        Loading...
                                    </div>
                                </div>
                            </col6>
                        </row>
                    </div>
                </col4>
            </row>
        </div>
        <div v-if="enriched_items.length > 0" class="cm-items-toolbar">
            <v-btn-toggle v-model="viewMode" mandatory density="compact" color="primary" class="cm-items-toolbar__group">
                <v-btn value="crops" size="small">{{ $t('confusion_matrix.view_crops') }}</v-btn>
                <v-btn value="full" size="small">{{ $t('confusion_matrix.view_full_image') }}</v-btn>
            </v-btn-toggle>
            <v-btn-toggle v-model="gridSize" mandatory density="compact" color="primary" class="cm-items-toolbar__group">
                <v-btn :value="2" size="small">{{ $t('confusion_matrix.grid_2_columns') }}</v-btn>
                <v-btn :value="4" size="small">{{ $t('confusion_matrix.grid_4_columns') }}</v-btn>
            </v-btn-toggle>
        </div>
        <v-table v-if="enriched_items.length > 0 && has_named_keys">
            <crops :model_id="model_id" :type="type" :title="type" :items="items_dict"
                   :view-mode="viewMode" :grid-size="gridSize"
                   @prediction_accepted="onPredictionAccepted"></crops>
        </v-table>
        <v-table v-else-if="enriched_items.length > 0">
            <crops :model_id="model_id" :type="type" v-for="pred_label in item_labels" :key="pred_label"
                   :title="pred_label" :items="items_dict[pred_label]"
                   :view-mode="viewMode" :grid-size="gridSize"
                   @prediction_accepted="onPredictionAccepted"></crops>
        </v-table>
    </div>
</template>

<script>
  import Crops from "@/components/confusion_matrix/Crops";
  import Row from "@/components/layout/Row";
  import Col4 from "@/components/layout/Col4";
  import Col6 from "@/components/layout/Col6";
  import LoadSettingsMixin from '@/mixins/LoadSettingsMixin';
  import CurrentUserMixin from '@/mixins/CurrentUserMixin.vue';
  import {
    getBlurUnlessMetaFromSettings,
    shouldBlurAssetMeta,
  } from '@/utils/sensitiveImageBlur';
  
  export default {
    name: "ConfusionMatrixItems",
    components: {Crops, Row, Col4, Col6},
    mixins: [LoadSettingsMixin, CurrentUserMixin],
    props: {
      model_id: {required: true},
      type: {required: true},
      items: {
        type: Array,
        default: () => [],
      },
    },
    emits: ['prediction_accepted'],
    data() {
      return {
        mostConfusedPairsData: [],
        labels_dict: {},
        labelFetchSeq: 0,
        viewMode: 'crops',
        gridSize: 4,
      }
    },
    computed: {
      enriched_items() {
        const items = Array.isArray(this.items) ? this.items : [];
        return items.map((item) => {
          const out = { ...item };
          if (item.label_id && this.labels_dict[item.label_id]) {
            out.label_name = this.labels_dict[item.label_id].name;
          }
          if (item.pred_label_id && this.labels_dict[item.pred_label_id]) {
            out.pred_label_name = this.labels_dict[item.pred_label_id].name;
          }
          return out;
        });
      },
      has_named_keys() {
        if(this.enriched_items.length === 0) {
          return false;
        }

        return !this.enriched_items[0].pred_label_name;
      },
      items_dict() {
        if (this.enriched_items.length === 0) {
          return {};
        }

        if (!this.enriched_items[0].pred_label_name) {
          return this.enriched_items;
        }

        let items_dict = {}
        this.enriched_items.forEach((item) => {
          let pred_label_name = item.pred_label_name;
          if(Object.keys(items_dict).indexOf(pred_label_name) === -1) {
            items_dict[pred_label_name] = []
          }
          items_dict[item.pred_label_name].push(item);
        })

        return items_dict;
      },
      item_labels() {
        return Object.keys(this.items_dict);
      },
      mostConfusedPairs() {
        if(this.type !== 'confused_with' || this.enriched_items.length === 0) {
          return [];
        }

        // Group items by (label_id, pred_label_id) pairs
        let pairsMap = {};
        this.enriched_items.forEach(item => {
          if(item.label_id && item.pred_label_id) {
            let key = `${item.label_id}_${item.pred_label_id}`;
            if(!pairsMap[key]) {
              pairsMap[key] = {
                originalLabel: item.label_name || this.labels_dict[item.label_id]?.name || 'Unknown',
                confusedLabel: item.pred_label_name || this.labels_dict[item.pred_label_id]?.name || 'Unknown',
                originalLabelId: item.label_id,
                confusedLabelId: item.pred_label_id,
                items: [],
                count: 0
              };
            }
            pairsMap[key].items.push(item);
            pairsMap[key].count++;
          }
        });

        // Convert to array and sort by count
        let pairs = Object.values(pairsMap);
        pairs.sort((a, b) => b.count - a.count);

        // Take top 3
        return pairs.slice(0, 3);
      }
    },
    watch: {
      items: {
        async handler(newItems) {
          const seq = ++this.labelFetchSeq;
          if (!newItems || newItems.length === 0) {
            this.labels_dict = {};
            return;
          }
          const ids = new Set();
          newItems.forEach((item) => {
            if (item.label_id) {
              ids.add(item.label_id);
            }
            if (item.pred_label_id) {
              ids.add(item.pred_label_id);
            }
          });
          const idList = Array.from(ids);
          if (idList.length === 0) {
            this.labels_dict = {};
            return;
          }
          try {
            const labels = (await this.$api.label.fetchByIds(idList)) || [];
            if (seq !== this.labelFetchSeq) {
              return;
            }
            const d = {};
            labels.forEach((l) => {
              d[l.id] = l;
            });
            this.labels_dict = d;
          } catch (e) {
            if (seq !== this.labelFetchSeq) {
              return;
            }
            this.labels_dict = {};
          }
        },
        immediate: true,
        deep: true,
      },
      mostConfusedPairs: {
        handler(newPairs) {
          if(newPairs.length > 0) {
            this.loadExamplesForPairs(newPairs);
          }
        },
        immediate: true
      }
    },
    methods: {
      onPredictionAccepted(payload) {
        this.$emit('prediction_accepted', payload);
      },
      sensitiveBlurForConfusionCrop(example) {
        const policy = getBlurUnlessMetaFromSettings(this.settings || []);
        return shouldBlurAssetMeta(example && example.meta, policy, this.is_sensitive_image_blur_exempt);
      },
      async loadExamplesForPairs(pairs) {
        // Initialize pairs data
        this.mostConfusedPairsData = pairs.map(pair => ({
          ...pair,
          predictedExamples: [],
          confusedExamples: []
        }));

        // Load examples for each pair
        for(let i = 0; i < this.mostConfusedPairsData.length; i++) {
          let pair = this.mostConfusedPairsData[i];
          
          // Load confused examples from the misclassified items
          let exampleItems = pair.items.slice(0, 3);
          for(let item of exampleItems) {
            try {
              let crop_data = await this.$api.asset.get_crop_for_conf_items('confused_with', JSON.stringify([item]));
              if(crop_data.crops && crop_data.crops.length > 0) {
                let crop = {
                  data: crop_data.crops[0].crop,
                  asset_id: crop_data.crops[0].asset_id,
                  meta: item.meta,
                };
                // These are the misclassified items (confused examples)
                pair.confusedExamples.push(crop);
              } else {
                let emptyCrop = { data: null, asset_id: item.asset_id, meta: item.meta };
                pair.confusedExamples.push(emptyCrop);
              }
            } catch(e) {
              let emptyCrop = { data: null, asset_id: item.asset_id, meta: item.meta };
              pair.confusedExamples.push(emptyCrop);
            }
          }

          // Load four random crops for the predicted label (what the model thought it was)
          let project_id = sessionStorage.getItem('project_id');
          try {
            let crop_data = await this.$api.label.get_random_crops(pair.confusedLabel, project_id, 4);
            let crops = (crop_data && crop_data.crops) || [];
            for (let j = 0; j < 4; j++) {
              if (crops[j] && crops[j].crop) {
                pair.predictedExamples.push({
                  data: crops[j].crop,
                  asset_id: crops[j].asset_id,
                });
              } else {
                pair.predictedExamples.push({ data: null, asset_id: null });
              }
            }
          } catch(e) {
            for (let j = 0; j < 4; j++) {
              pair.predictedExamples.push({ data: null, asset_id: null });
            }
          }
        }

        // Force reactivity update
        this.$forceUpdate();
      }
    },
    mounted() {
      if(this.type === 'confused_with' && this.mostConfusedPairs.length > 0) {
        this.loadExamplesForPairs(this.mostConfusedPairs);
      }
    }
  }
</script>

<style scoped>
.cm-example {
  margin-bottom: 10px;
}
.cm-example--blur img {
  filter: blur(28px);
  transform: scale(1.02);
}
.cm-items-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}
.cm-items-toolbar__group {
  flex-shrink: 0;
}
</style>